import { Box, Typography } from '@mui/material';
import classNames from 'classnames/bind';
import { useTranslation } from 'react-i18next';
import { Bar, BarChart, CartesianGrid, Tooltip, XAxis, YAxis } from 'recharts';
import styles from '../Dashboard.module.scss';
import { useResponsiveValue } from '~/hooks/useResponsiveValue';
import { ISupplierStatistic } from '~/utils/interface/dashboard';
const cx = classNames.bind(styles);

const FacilityDevice = (props: { supplierStatistic: ISupplierStatistic[] }) => {
  const { t } = useTranslation('admin/home');
  const maxValue = Math.max(...props.supplierStatistic.map((d) => d.device_count));
  const step = 20;
  const maxTick = Math.ceil(maxValue / step) * step;
  const ticks = Array.from({ length: maxTick / step + 1 }, (_, i) => i * step);

  const responsive = useResponsiveValue({
    lg: {
      barWidth: 20,
      gapWidth: 5,
      height: 300,
      fontSize: 10,
    },
    xl: {
      barWidth: 30,
      gapWidth: 10,
      height: 380,
      fontSize: 12,
    },
  });
  function acronym(str: string): string {
    const [first] = str.split('-');
    const parts = first.split(' ');
    const last = parts[parts.length - 1];

    const letters = parts
      .slice(0, -1)
      .map((word) => word[0]?.toUpperCase() ?? '')
      .join('');

    let result = letters + last;

    if (result.length >= 10) {
      result = result.substring(0, 10 - 3) + '...';
    }

    return result;
  }

  const aggregatedData = props.supplierStatistic.reduce((acc: any, { agent_code, device_count, supplier }) => {
    if (acc[agent_code]) {
      acc[agent_code].device_count += device_count;
    } else {
      acc[agent_code] = { agent_code, device_count, supplier };
    }
    return acc;
  }, {});

  const modifiedData = Object.values(aggregatedData);

  return (
    <Box className={cx('w-[36%] h-full', 'dashboard-widget')}>
      <Typography className={cx('dashboard-widget-title', 'dashboard-widget-title-text')}>{t('agency')}</Typography>
      <Box className={cx('dashboard-widget-body')}>
        <Box className={cx('!overflow-x-auto pt-5')}>
          <BarChart
            width={modifiedData.length * ((responsive?.barWidth ?? 45) + (responsive?.gapWidth ?? 30)) + 80}
            height={responsive?.height}
            data={modifiedData}
            margin={{
              top: 5,
              right: 30,
              left: 20,
              bottom: 20,
            }}
            barGap={responsive?.gapWidth ?? 30}
            barSize={responsive?.barWidth ?? 45}
          >
            <CartesianGrid strokeDasharray='3 3' horizontal={true} vertical={true} />
            <XAxis
              dataKey={'agent_code'}
              tickLine={false}
              angle={-45}
              tickFormatter={(value) => acronym(value)}
              dy={10}
              fontSize={responsive?.fontSize ?? 12}
            />
            <YAxis domain={[0, maxTick]} ticks={ticks} width={30} fontSize={responsive?.fontSize ?? 12} />
            <Tooltip labelFormatter={(label: any, payload: any) => payload?.[0]?.payload?.supplier || label || ''} />
            <Bar
              name={t('device_count')}
              dataKey='device_count'
              fill='#ED1002'
              fillOpacity={0.8}
              background={{ fill: '#eee' }}
              barSize={25}
            />
          </BarChart>
        </Box>
        <Box className='flex justify-center mt-3 items-center gap-3'>
          <Box className='lg:w-[7px] xl:w-[10px] lg:h-[7px] xl:h-[10px] bg-[#ED1002]'></Box>
          <Typography className='m-0 lg:text-[10px] xl:text-[12px]'>
            {t('facilityDeviceComponent.deviceCount')}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default FacilityDevice;
